/**
 * Who pays the list price net of VAT — the checkout's mirror of the server's
 * rule (`recipientVat` + `buyerPriceFactor` in
 * packages/billing/src/vat-calculator.ts, applied to every charge by
 * apps/api/src/lib/billing/buyer-price.ts). The server decides the real
 * charge; this only lets the checkout show it.
 *
 * List prices include the seller's 20% Bulgarian VAT (Appy Solutions, BG). A
 * VAT-registered EU company outside Bulgaria whose number VIES confirmed is
 * invoiced at 0% (reverse charge) and pays the list price / 1.2 (Tudor,
 * 2026-10-27).
 */

export const BG_VAT_RATE = 20;

/** The share of a list price a reverse-charge buyer pays. */
export const REVERSE_CHARGE_FACTOR = 1 / (1 + BG_VAT_RATE / 100);

/** EU member states — ISO codes, plus EL (how VIES writes Greece). */
export const EU_COUNTRIES = new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "EL",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
]);

/** VIES writes Greece as EL (`vatCountryPrefix` in packages/billing). */
export function vatCountryPrefix(country: string): string {
  const code = country.toUpperCase();
  return code === "GR" ? "EL" : code;
}

/**
 * `vatNumber` the way the server stores it (`normalizeVatNumber` in
 * apps/api/src/lib/billing/vies.ts): two leading letters are the prefix
 * (GR → EL), anything else is the national part and takes `country`'s prefix.
 */
export function withVatPrefix(vatNumber: string, country: string): string {
  const compact = vatNumber.replace(/[\s.-]/g, "").toUpperCase();
  if (!/^[A-Z]{2}/.test(compact)) return vatCountryPrefix(country) + compact;
  return compact.startsWith("GR") ? `EL${compact.slice(2)}` : compact;
}

export interface VatBuyer {
  country: string;
  fiscalType: string;
  vatNumber?: string | null;
  /** What VIES said of `vatNumber` — only "valid" earns reverse charge. */
  vatStatus?: string | null;
}

/**
 * Whether `buyer`'s invoice reverse-charges: an EU company outside BG with a
 * VIES-valid number of its own country. A blank country or fiscal type reads
 * as the invoice prints it (recipientVat): RO, an individual.
 */
export function isReverseChargeBuyer(buyer: VatBuyer | null | undefined): boolean {
  if (!buyer || (buyer.fiscalType || "individual") === "individual" || !buyer.vatNumber) {
    return false;
  }
  const country = (buyer.country || "RO").toUpperCase();
  if (country === "BG" || !EU_COUNTRIES.has(country)) return false;
  return (
    buyer.vatStatus === "valid" &&
    withVatPrefix(buyer.vatNumber, country).startsWith(vatCountryPrefix(country))
  );
}

/** The share of the list price `buyer` pays — 1 unless the invoice reverse-charges. */
export function buyerPriceFactorOf(buyer: VatBuyer | null | undefined): number {
  return isReverseChargeBuyer(buyer) ? REVERSE_CHARGE_FACTOR : 1;
}

/** `amount` × `factor` in whole cents — `applyBuyerPriceFactor` on the server. */
export function applyBuyerPriceFactor(amount: number, factor: number): number {
  if (factor === 1) return amount;
  return Math.round(amount * factor * 100) / 100;
}
